import React from 'react'
import throttle from 'lodash.throttle'
import { Block } from '@news-mono/web-common'
import { extractTtsText, preprocessTtsData } from '../TtsText'
import type { TtsParagraphWithWords, TtsTextStructure } from '../TtsText'
import { useTtsPlayerState } from '../TtsPlayer/useTtsPlayerState'
import type { PresignedUrlResponse } from '../TtsPlayer/types'
import {
    doesBlockContributeToTts,
    findCurrentParagraphElement,
    computeFloatingPosition,
    getDefaultFloatingPosition,
    POSITION_UPDATE_INTERVAL_MS,
} from './ttsHelpers'

export const ARTICLE_CONTENT_ID = 'ArticleContent'

export interface UseArticleTtsOptions {
    renderTarget: string
    isArticleTtsEnabled: boolean
    /** ID of the article content DOM element (for floating controls positioning). Defaults to ARTICLE_CONTENT_ID. */
    articleContentID?: string
}

export interface UseArticleTtsResult {
    canPlayTts: boolean
    isTtsExpanded: boolean
    preprocessedTtsData: TtsParagraphWithWords[]
    tts: ReturnType<typeof useTtsPlayerState>
    ttsHeaderRef: React.RefObject<HTMLElement>
    showFloatingTtsControls: boolean
    floatingControlsPosition: { top: number; left: number } | null
    handleToggleTtsPlayback: () => void
    handleExpandTtsPlayer: () => void
    handleStopTts: () => void
    handleTtsPlayTriggered: () => void
    doesBlockContributeToTts: (block: Block) => boolean
}

export function useArticleTts(
    blocks: Block[],
    ttsPresignedUrls: PresignedUrlResponse | undefined,
    options: UseArticleTtsOptions,
): UseArticleTtsResult {
    const {
        renderTarget,
        isArticleTtsEnabled,
        articleContentID = ARTICLE_CONTENT_ID,
    } = options

    const hasTtsUrls = Boolean(
        ttsPresignedUrls?.audioUrl && ttsPresignedUrls?.speechMarksUrl,
    )
    const ttsText = React.useMemo<TtsTextStructure>(
        () =>
            hasTtsUrls ? extractTtsText(blocks) : { body: '', paragraphs: [] },
        [blocks, hasTtsUrls],
    )
    const canPlayTts =
        isArticleTtsEnabled &&
        renderTarget !== 'amp' &&
        hasTtsUrls &&
        ttsText.body.length > 0

    const [isTtsExpanded, setIsTtsExpanded] = React.useState(false)
    const tts = useTtsPlayerState(
        isArticleTtsEnabled ? ttsPresignedUrls || {} : {},
        { isPlayerExpanded: isTtsExpanded },
    )

    const preprocessedTtsData = React.useMemo<TtsParagraphWithWords[]>(() => {
        if (
            !canPlayTts ||
            !tts.allSpeechMarks ||
            tts.allSpeechMarks.length === 0
        ) {
            return []
        }
        return preprocessTtsData(tts.allSpeechMarks, ttsText)
    }, [canPlayTts, tts.allSpeechMarks, ttsText])

    const ttsHeaderRef = React.useRef<HTMLElement>(null!)
    const lastFloatingPositionRef = React.useRef<{
        top: number
        left: number
    } | null>(null)
    const [showFloatingTtsControls, setShowFloatingTtsControls] =
        React.useState(false)
    const [floatingControlsPosition, setFloatingControlsPosition] =
        React.useState<{ top: number; left: number } | null>(null)
    const [isPlayerVisible, setIsPlayerVisible] = React.useState(true)
    const [hasPlaybackTriggered, setHasPlaybackTriggered] =
        React.useState(false)

    React.useEffect(() => {
        if (!canPlayTts || !isTtsExpanded || typeof window === 'undefined') {
            return
        }
        const el = ttsHeaderRef.current
        if (!el || !('IntersectionObserver' in window)) {
            return
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0]
                setIsPlayerVisible(entry.isIntersecting)
            },
            { threshold: 0.1 },
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [canPlayTts, isTtsExpanded])

    React.useEffect(() => {
        if (isPlayerVisible) {
            setShowFloatingTtsControls(false)
            setFloatingControlsPosition(null)
        }
    }, [isPlayerVisible])

    React.useEffect(() => {
        if (
            !canPlayTts ||
            typeof window === 'undefined' ||
            isPlayerVisible ||
            !hasPlaybackTriggered
        ) {
            return
        }

        const updateButtonPosition = () => {
            if (!canPlayTts || isPlayerVisible || !hasPlaybackTriggered) {
                lastFloatingPositionRef.current = null
                setShowFloatingTtsControls(false)
                setFloatingControlsPosition(null)
                return
            }
            const paragraph = findCurrentParagraphElement(articleContentID)
            if (!paragraph) {
                setShowFloatingTtsControls(true)
                const fallback =
                    lastFloatingPositionRef.current ??
                    getDefaultFloatingPosition(
                        window.innerWidth,
                        window.innerHeight,
                    )
                setFloatingControlsPosition(fallback)
                return
            }
            const rect = paragraph.getBoundingClientRect()
            const pos = computeFloatingPosition(
                rect,
                window.innerWidth,
                window.innerHeight,
            )
            lastFloatingPositionRef.current = pos
            setShowFloatingTtsControls(true)
            setFloatingControlsPosition(pos)
        }

        updateButtonPosition()
        let lastPositionUpdateAt = 0

        const handleScroll = throttle(() => {
            if (!canPlayTts || isPlayerVisible || !hasPlaybackTriggered) return
            updateButtonPosition()
        }, POSITION_UPDATE_INTERVAL_MS)

        const handleResize = throttle(() => {
            if (canPlayTts && !isPlayerVisible && hasPlaybackTriggered) {
                updateButtonPosition()
            }
        }, POSITION_UPDATE_INTERVAL_MS)

        window.addEventListener('scroll', handleScroll, { passive: true })
        window.addEventListener('resize', handleResize)

        let rafId: number | null = null
        const updateLoop = () => {
            if (
                !canPlayTts ||
                isPlayerVisible ||
                !hasPlaybackTriggered ||
                !tts.isPlaying
            ) {
                setShowFloatingTtsControls(false)
                setFloatingControlsPosition(null)
                return
            }
            const now = Date.now()
            if (now - lastPositionUpdateAt >= POSITION_UPDATE_INTERVAL_MS) {
                lastPositionUpdateAt = now
                updateButtonPosition()
            }
            rafId = requestAnimationFrame(updateLoop)
        }
        if (tts.isPlaying) {
            rafId = requestAnimationFrame(updateLoop)
        }

        return () => {
            handleScroll.cancel()
            handleResize.cancel()
            window.removeEventListener('scroll', handleScroll)
            window.removeEventListener('resize', handleResize)
            if (rafId !== null) {
                cancelAnimationFrame(rafId)
            }
        }
    }, [
        canPlayTts,
        isPlayerVisible,
        hasPlaybackTriggered,
        tts.isPlaying,
        articleContentID,
    ])

    const handleToggleTtsPlayback = React.useCallback(() => {
        if (!canPlayTts) return
        const audio = tts.audioRef.current
        if (!audio) return
        if (audio.paused) {
            void audio.play()
        } else {
            audio.pause()
        }
    }, [canPlayTts, tts.audioRef])

    const handleExpandTtsPlayer = React.useCallback(() => {
        if (!canPlayTts) return
        setIsTtsExpanded(true)
    }, [canPlayTts])

    const handleStopTts = React.useCallback(() => {
        tts.stopAndReset()
        lastFloatingPositionRef.current = null
        setHasPlaybackTriggered(false)
        setShowFloatingTtsControls(false)
        setFloatingControlsPosition(null)
        setIsTtsExpanded(false)
    }, [tts])

    const handleTtsPlayTriggered = React.useCallback(() => {
        setHasPlaybackTriggered(true)
    }, [])

    return {
        canPlayTts,
        isTtsExpanded,
        preprocessedTtsData,
        tts,
        ttsHeaderRef,
        showFloatingTtsControls,
        floatingControlsPosition,
        handleToggleTtsPlayback,
        handleExpandTtsPlayer,
        handleStopTts,
        handleTtsPlayTriggered,
        doesBlockContributeToTts,
    }
}
